import { Recycle, WarningCircle } from "@phosphor-icons/react";

const selectClass =
  "h-8 rounded-md border border-[#D0D5DD] bg-white px-2 text-[12px] outline-none focus:border-[#0E7C86] focus:ring-1 focus:ring-[#0E7C86] transition-colors";

export const ByproductPanel = ({ plan, steps, options, acknowledged, onAcknowledge, onOverride }) => {
  if (!plan?.length) return null;
  // Linking works in SAP element IDs, the Excel flow in builder step numbers -
  // same panel either way, the caller says which.
  const choices = options
    ?? (steps || [])
      .filter((s) => s.step_type === "Production")
      .map((s) => ({ value: s.step_no, label: `${s.step_no} - ${s.description.slice(0, 40)}` }));
  const chosen = (entry) => (options ? entry.element_id : entry.step_no) ?? "";
  return (
    <div
      className="rounded-lg border border-[#FEDF89] bg-[#FFFAEB] p-4"
      data-testid="routing-byproduct-panel"
    >
      <div className="flex items-center gap-2">
        <Recycle size={16} weight="fill" className="text-[#B54708]" />
        <h3 className="font-heading text-[14px] font-semibold text-[#B54708]">
          By-products on this model
        </h3>
      </div>
      <p className="mt-1 text-[12px] text-[#B54708]">
        SAP ties a by-product to one operation of the routing, so a new routing cannot inherit it. Confirm
        which step each one comes off - it is re-applied for you, and if SAP will not allow that yet you
        will be told exactly what to set.
      </p>

      <div className="mt-3 space-y-2">
        {plan.map((entry) => (
          <div
            key={entry.product_id}
            className="flex flex-wrap items-center gap-3 rounded-md bg-white border border-[#FEDF89] px-3 py-2"
            data-testid={`routing-byproduct-${entry.product_id}`}
          >
            <span className="font-mono text-[12px] font-semibold text-[#1D2939]">{entry.product_id}</span>
            {entry.was_at_element_id && (
              <span className="text-[11px] text-[#667085]">
                was at {entry.was_at_element_id}
                {entry.activity_id ? ` / activity ${entry.was_at_activity_id}` : ""}
              </span>
            )}
            <div className="flex items-center gap-2 ml-auto min-w-0">
              <label className="text-[11px] text-[#475467] shrink-0">
                {options ? "Comes off operation" : "Comes off step"}
              </label>
              <select
                value={chosen(entry)}
                onChange={(e) => onOverride(entry.product_id, e.target.value)}
                data-testid={`routing-byproduct-step-${entry.product_id}`}
                className={`${selectClass} min-w-0 max-w-[190px] sm:max-w-[320px]`}
              >
                {choices.map((choice) => (
                  <option key={choice.value} value={choice.value}>
                    {choice.label}
                  </option>
                ))}
              </select>
            </div>
            {!chosen(entry) && (
              <span className="flex items-center gap-1 text-[11px] text-[#B42318]">
                <WarningCircle size={12} /> no production step to attach it to
              </span>
            )}
          </div>
        ))}
      </div>

      <label className="mt-3 flex items-start gap-2 cursor-pointer" data-testid="routing-byproduct-ack-label">
        <input
          type="checkbox"
          checked={acknowledged}
          onChange={(e) => onAcknowledge(e.target.checked)}
          data-testid="routing-byproduct-ack"
          className="mt-0.5 h-4 w-4 rounded border-[#D0D5DD] text-[#0E7C86] focus:ring-[#0E7C86]"
        />
        <span className="text-[12px] text-[#B54708]">
          I have checked where {plan.map((p) => p.product_id).join(", ")} comes off. Posting without this is
          blocked, so no scrap assignment is ever lost without someone seeing it.
        </span>
      </label>
    </div>
  );
};
